import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { CardSkeleton, ProfileCardSkeleton } from '../components/LoadingSkeleton';
import { resolveImageUrl, NEWS_PLACEHOLDER, PROFILE_PLACEHOLDER } from '../utils/imageHelper';

const HomePage = () => {
  const { currentUser } = useAuth();
  const { error } = useToast();
  const [data, setData] = useState({
    latestNews: [],
    matrimonyHighlights: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: res } = await api.get('/homepage-data');
        setData({
          latestNews: Array.isArray(res.latestNews) ? res.latestNews : [],
          matrimonyHighlights: Array.isArray(res.matrimonyHighlights) ? res.matrimonyHighlights : [],
        });
      } catch (err) {
        console.error('Homepage fetch error:', err);
        error('Failed to load some content. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [error]);

  const formatDate = (post) => {
    if (post.createdAt?.seconds) {
      return new Date(post.createdAt.seconds * 1000).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    }
    if (typeof post.createdAt === 'string') {
      return new Date(post.createdAt).toLocaleDateString('en-IN');
    }
    return 'Recent';
  };

  const getPostImage = (post) =>
    resolveImageUrl(post.images?.[0] || post.imageUrl || post.image, NEWS_PLACEHOLDER);

  const getProfileImage = (profile) =>
    resolveImageUrl(profile.profilePhotoUrl, PROFILE_PLACEHOLDER);

  return (
    <div className="homepage">

      {/* ===================== HERO ===================== */}
      <section className="hero">
        <div className="hero__bg-pattern" />
        <div className="container">
          <div className="hero__grid">
            {/* Left */}
            <div className="hero__text">
              <div className="hero__eyebrow">
                <span className="hero__pot">🏺</span>
                <span>Kumbhar Samaj Onnati Mandal</span>
              </div>

              <h1 className="hero__title">
                एका धाग्याने जोडलेलो,<br />
                <span className="hero__title--highlight">एकत्र वाढूया</span>
              </h1>

              <p className="hero__subtitle">
                Connecting thousands of Kumbhar families across Maharashtra and beyond —
                through matrimony, news, and community bonds.
              </p>

              <div className="hero__actions">
                <Link to="/matrimony" className="btn btn-primary btn-lg hero__btn-primary">
                  💑 Find a Match
                </Link>
                <Link to="/matrimony/create" className="btn btn-outline btn-lg">
                  🏺 Create Profile
                </Link>
              </div>

              {/* Stats */}
              {/* <div className="hero__stats">
                <div className="hero__stat">
                  <span className="hero__stat-number">1000+</span>
                  <span className="hero__stat-label">Active Members</span>
                </div>
                <div className="hero__stat-divider" />
                <div className="hero__stat">
                  <span className="hero__stat-number">500+</span>
                  <span className="hero__stat-label">Profiles</span>
                </div>
                <div className="hero__stat-divider" />
                <div className="hero__stat">
                  <span className="hero__stat-number">50+</span>
                  <span className="hero__stat-label">Cities</span>
                </div>
              </div> */}
            </div>

            {/* Right — Visual Card */}
            <div className="hero__visual">
              <div className="hero__card">
                <div className="hero__card-inner">
                  {/* Top row */}
                  <div className="hero__card-header">
                    <div className="hero__card-icon">🏺</div>
                    <div>
                      <div className="hero__card-title">Kumbhar Samaj Onnati Mandal</div>
                      <div className="hero__card-sub">Since Generations</div>
                    </div>
                  </div>

                  {/* Decorative pottery pattern */}
                  <div className="hero__pottery-art">
                    <div className="pottery-ring pottery-ring--1" />
                    <div className="pottery-ring pottery-ring--2" />
                    <div className="pottery-ring pottery-ring--3" />
                    <span className="pottery-center">🏺</span>
                  </div>

                  {/* Quote */}
                  <div className="hero__card-quote">
                    <p>
                      "Sant Gora Kumbhar taught us that true craft
                      lies not just in clay, but in character."
                    </p>
                    <span>— Bhakti Tradition of Maharashtra</span>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="hero__float hero__float--top">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  Verified Profiles
                </div>
                <div className="hero__float hero__float--bottom">
                  💑 New matches daily
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== HERITAGE BAND ===================== */}
      <div className="heritage-band">
        <div className="container">
          <div className="heritage-band__inner">
            <div className="heritage-band__item">
              <span>🏺</span>
              <span>Ancient Artisan Community</span>
            </div>
            <div className="heritage-band__dot" />
            <div className="heritage-band__item">
              <span>🕉️</span>
              <span>Followers of Sant Gora Kumbhar</span>
            </div>
            <div className="heritage-band__dot" />
            <div className="heritage-band__item">
              <span>🌏</span>
              <span>Spread across Maharashtra & India</span>
            </div>
            <div className="heritage-band__dot" />
            <div className="heritage-band__item">
              <span>🎓</span>
              <span>Education, Progress & Tradition</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-eyebrow">Simple Process</span>
            <h2 className="section-title">How Matrimony Works</h2>
            <p className="section-subtitle">
              Three simple steps to find your perfect Kumbhar match
            </p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-card__number">1</div>
              <div className="step-card__icon">📝</div>
              <h3>Create Profile</h3>
              <p>
                Register and fill in your 8-section matrimony profile with
                personal, family, and community details.
              </p>
              <Link to="/register" className="step-card__link">
                Get Started →
              </Link>
            </div>

            <div className="step-card step-card--featured">
              <div className="step-card__number">2</div>
              <div className="step-card__icon">🔍</div>
              <h3>Browse & Match</h3>
              <p>
                Search verified Kumbhar profiles filtered by city, age,
                education, gotra, and more.
              </p>
              <Link to="/matrimony" className="step-card__link">
                Browse Profiles →
              </Link>
            </div>

            <div className="step-card">
              <div className="step-card__number">3</div>
              <div className="step-card__icon">💑</div>
              <h3>Connect</h3>
              <p>
                Express interest and connect with families.
                Begin your journey towards a happy married life.
              </p>
              <Link to="/matrimony/create" className="step-card__link">
                Create Profile →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== MATRIMONY SPOTLIGHT ===================== */}
      <section className="section section--tinted">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-eyebrow">Matrimony</span>
              <h2 className="section-title">Recent Profiles</h2>
              <p className="section-subtitle">
                Community members looking for their perfect match
              </p>
            </div>
            <Link to="/matrimony" className="btn btn-outline">
              View All Profiles
            </Link>
          </div>

          {loading ? (
            <div className="profile-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <ProfileCardSkeleton key={i} />
              ))}
            </div>
          ) : data.matrimonyHighlights.length > 0 ? (
            <div className="profile-grid">
              {data.matrimonyHighlights.slice(0, 6).map((profile) => (
                <Link
                  key={profile.id}
                  to={`/matrimony/${profile.id}`}
                  className="profile-card"
                >
                  <div className="profile-card__photo">
                    <img
                      src={getProfileImage(profile)}
                      alt={profile.fullName}
                      onError={(e) => { e.target.src = PROFILE_PLACEHOLDER; }}
                    />
                    <div className="profile-card__verified">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="profile-card__info">
                    <h3>{profile.fullName}</h3>
                    <p className="profile-card__meta">
                      {profile.age} yrs • {profile.gender}
                    </p>
                    <p className="profile-card__location">
                      📍 {profile.currentResidence || profile.city || 'Maharashtra'}
                    </p>
                    {profile.education && (
                      <p className="profile-card__edu">🎓 {profile.education}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-profiles">
              <span>🏺</span>
              <h3>Be the First!</h3>
              <p>No profiles yet. Create yours and start the journey.</p>
              <Link to="/matrimony/create" className="btn btn-primary">
                Create Matrimony Profile
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===================== NEWS SECTION ===================== */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-eyebrow">Community Updates</span>
              <h2 className="section-title">Latest News</h2>
              <p className="section-subtitle">
                Stay informed about events and achievements
              </p>
            </div>
            <Link to="/news" className="btn btn-outline">
              View All News
            </Link>
          </div>

          {loading ? (
            <div className="news-grid">
              {[1, 2, 3, 4].map((i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : data.latestNews.length > 0 ? (
            <div className="news-grid">
              {/* Featured first post */}
              {data.latestNews.slice(0, 1).map((post) => (
                <Link
                  key={post.id}
                  to={`/post/${post.id}`}
                  className="news-card news-card--featured"
                >
                  <div className="news-card__image">
                    <img
                      src={getPostImage(post)}
                      alt={post.title}
                      onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
                    />
                  </div>
                  <div className="news-card__body">
                    <div className="news-card__meta">
                      <span className="news-category">{post.category || 'News'}</span>
                      <span className="news-date">{formatDate(post)}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>
                      {post.content
                        ? post.content.replace(/<[^>]*>/g, '').substring(0, 120) + '...'
                        : ''}
                    </p>
                    <span className="news-card__read-more">Read More →</span>
                  </div>
                </Link>
              ))}

              {/* Remaining posts */}
              {data.latestNews.slice(1, 4).map((post) => (
                <Link
                  key={post.id}
                  to={`/post/${post.id}`}
                  className="news-card"
                >
                  <div className="news-card__image">
                    <img
                      src={getPostImage(post)}
                      alt={post.title}
                      onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
                    />
                  </div>
                  <div className="news-card__body">
                    <div className="news-card__meta">
                      <span className="news-category">{post.category || 'News'}</span>
                      <span className="news-date">{formatDate(post)}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>
                      {post.content
                        ? post.content.replace(/<[^>]*>/g, '').substring(0, 80) + '...'
                        : ''}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-news">
              <span>📰</span>
              <h3>No news yet</h3>
              <p>Check back soon for community updates.</p>
            </div>
          )}
        </div>
      </section>

      {/* ===================== COMMUNITY VALUES ===================== */}
      <section className="section section--tinted">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-eyebrow">Our Heritage</span>
            <h2 className="section-title">What Unites Us</h2>
            <p className="section-subtitle">
              The values and traditions that define the Kumbhar community
            </p>
          </div>

          <div className="values-grid">
            {[
              { icon: '🏺', title: 'Pottery Heritage', desc: 'Thousands of years of sacred artisan tradition, making vessels that nourished civilisations.' },
              { icon: '👨‍👩‍👧', title: 'Family First', desc: 'Strong family ties and respect for elders remain the cornerstone of our community values.' },
              { icon: '🕉️', title: 'Spiritual Legacy', desc: 'Inspired by Sant Gora Kumbhar\'s devotion to Lord Vitthal and the Bhakti movement.' },
              { icon: '🎓', title: 'Education & Progress', desc: 'Embracing modern education while preserving our rich cultural and artisan heritage.' },
              { icon: '🤝', title: 'Community Support', desc: 'Standing together in times of need — from Maharashtra to every corner of India.' },
              { icon: '🌱', title: 'Growing Together', desc: 'From traditional pottery to IT, healthcare, and business — our community evolves.' },
            ].map((value) => (
              <div key={value.title} className="value-card">
                <div className="value-card__icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="cta-section">
        <div className="cta-section__bg" />
        <div className="container">
          <div className="cta-section__content">
            <span className="cta-section__pot">🏺</span>
            <h2>
              {currentUser
                ? `Welcome back, ${currentUser.displayName?.split(' ')[0] || 'Member'}!`
                : 'Join Our Growing Community'}
            </h2>
            <p>
              {currentUser
                ? 'Find your perfect match or stay updated with the latest community news.'
                : 'Connect with thousands of Kumbhar families. Create your matrimony profile today.'}
            </p>
            <div className="cta-section__actions">
              {currentUser ? (
                <>
                  <Link to="/matrimony" className="btn btn-gold btn-lg">
                    💑 Browse Profiles
                  </Link>
                  <Link to="/news" className="btn btn-outline-light btn-lg">
                    📰 Read News
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" className="btn btn-gold btn-lg">
                    🏺 Join Free Today
                  </Link>
                  <Link to="/login" className="btn btn-outline-light btn-lg">
                    Already a Member? Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STYLES ===================== */}
      <style jsx>{`
        .homepage {
          min-height: 100vh;
        }

        /* ---- Shared Section ---- */
        .section {
          padding: var(--space-20) 0;
        }
        .section--tinted {
          background: linear-gradient(180deg, var(--neutral-100) 0%, var(--primary-50) 100%);
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: var(--space-10);
          gap: var(--space-4);
        }
        .section-header.text-center {
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .section-eyebrow {
          display: block;
          font-size: var(--text-xs);
          font-weight: var(--weight-bold);
          color: var(--primary-500);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: var(--space-2);
        }
        .section-title {
          font-size: var(--text-4xl);
          color: var(--primary-900);
          margin: 0 0 var(--space-2);
          line-height: var(--leading-tight);
        }
        .section-subtitle {
          font-size: var(--text-lg);
          color: var(--neutral-600);
          margin: 0;
        }

        /* ---- HERO ---- */
        .hero {
          position: relative;
          background: linear-gradient(135deg, var(--neutral-50) 0%, var(--primary-50) 50%, var(--gold-100) 100%);
          padding: var(--space-20) 0;
          overflow: hidden;
        }
        .hero__bg-pattern {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='30' cy='30' r='1' fill='%23C96843' fill-opacity='0.08'/%3E%3C/svg%3E");
          pointer-events: none;
        }
        .hero__grid {
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: var(--space-16);
          align-items: center;
          position: relative;
          z-index: 1;
        }

        /* Hero Text */
        .hero__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          background: white;
          border: 1px solid var(--primary-200);
          color: var(--primary-700);
          padding: var(--space-2) var(--space-4);
          border-radius: var(--radius-full);
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          margin-bottom: var(--space-6);
          box-shadow: var(--shadow-xs);
        }
        .hero__pot {
          font-size: var(--text-lg);
        }
        .hero__title {
          font-size: var(--text-6xl);
          line-height: var(--leading-tight);
          color: var(--primary-900);
          margin-bottom: var(--space-6);
        }
        .hero__title--highlight {
          color: var(--primary-500);
          position: relative;
        }
        .hero__title--highlight::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--gold-500);
          border-radius: var(--radius-full);
          opacity: 0.6;
        }
        .hero__subtitle {
          font-size: var(--text-lg);
          color: var(--neutral-600);
          line-height: var(--leading-relaxed);
          margin-bottom: var(--space-8);
          max-width: 520px;
        }
        .hero__actions {
          display: flex;
          gap: var(--space-4);
          margin-bottom: var(--space-10);
          flex-wrap: wrap;
        }
        .hero__btn-primary {
          background: linear-gradient(135deg, var(--primary-600), var(--primary-500));
          box-shadow: var(--shadow-primary);
        }

        /* Hero Stats */
        .hero__stats {
          display: flex;
          align-items: center;
          gap: var(--space-6);
          padding: var(--space-5) var(--space-6);
          background: white;
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
          width: fit-content;
        }
        .hero__stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .hero__stat-number {
          font-family: var(--font-display);
          font-size: var(--text-3xl);
          font-weight: var(--weight-bold);
          color: var(--primary-600);
          line-height: 1;
        }
        .hero__stat-label {
          font-size: var(--text-xs);
          color: var(--neutral-500);
          margin-top: var(--space-1);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: var(--weight-medium);
        }
        .hero__stat-divider {
          width: 1px;
          height: 40px;
          background: var(--neutral-200);
        }

        /* Hero Visual Card */
        .hero__visual {
          display: flex;
          justify-content: center;
        }
        .hero__card {
          position: relative;
          width: 100%;
          max-width: 400px;
        }
        .hero__card-inner {
          background: white;
          border-radius: var(--radius-2xl);
          padding: var(--space-8);
          box-shadow: var(--shadow-xl);
          border: 1px solid var(--neutral-200);
        }
        .hero__card-header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-4);
          border-bottom: 1px solid var(--neutral-200);
        }
        .hero__card-icon {
          font-size: 2.5rem;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary-50);
          border-radius: var(--radius-lg);
          border: 1px solid var(--primary-200);
        }
        .hero__card-title {
          font-family: var(--font-display);
          font-size: var(--text-xl);
          font-weight: var(--weight-bold);
          color: var(--primary-800);
        }
        .hero__card-sub {
          font-size: var(--text-sm);
          color: var(--gold-700);
          font-weight: var(--weight-medium);
        }

        /* Pottery Art */
        .hero__pottery-art {
          position: relative;
          height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: var(--space-6);
        }
        .pottery-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid var(--primary-200);
          animation: rotate 12s linear infinite;
        }
        .pottery-ring--1 {
          width: 140px;
          height: 140px;
          border-color: var(--primary-100);
          border-style: dashed;
          animation-duration: 20s;
        }
        .pottery-ring--2 {
          width: 100px;
          height: 100px;
          border-color: var(--gold-300);
          animation-direction: reverse;
          animation-duration: 15s;
        }
        .pottery-ring--3 {
          width: 60px;
          height: 60px;
          border-color: var(--primary-200);
          animation-duration: 10s;
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .pottery-center {
          font-size: 2.5rem;
          z-index: 1;
          animation: float 3s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        .hero__card-quote {
          background: var(--primary-50);
          border-left: 4px solid var(--primary-500);
          border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
          padding: var(--space-4);
        }
        .hero__card-quote p {
          font-size: var(--text-sm);
          color: var(--primary-700);
          font-style: italic;
          line-height: var(--leading-relaxed);
          margin: 0 0 var(--space-2);
        }
        .hero__card-quote span {
          font-size: var(--text-xs);
          color: var(--neutral-500);
          font-weight: var(--weight-medium);
        }

        /* Floating Badges */
        .hero__float {
          position: absolute;
          background: white;
          padding: var(--space-2) var(--space-4);
          border-radius: var(--radius-full);
          box-shadow: var(--shadow-md);
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          display: flex;
          align-items: center;
          gap: var(--space-2);
          border: 1px solid var(--neutral-200);
          white-space: nowrap;
        }
        .hero__float--top {
          top: -16px;
          right: 20px;
          color: var(--success);
          animation: float 3.5s ease-in-out infinite;
        }
        .hero__float--bottom {
          bottom: -16px;
          left: 20px;
          color: var(--primary-600);
          animation: float 4s ease-in-out infinite 0.5s;
        }

        /* ---- HERITAGE BAND ---- */
        .heritage-band {
          background: linear-gradient(90deg, var(--primary-800), var(--primary-700), var(--gold-700), var(--primary-700), var(--primary-800));
          padding: var(--space-4) 0;
        }
        .heritage-band__inner {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: var(--space-6);
          flex-wrap: wrap;
        }
        .heritage-band__item {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          color: rgba(255,255,255,0.9);
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
        }
        .heritage-band__dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--gold-400);
          opacity: 0.7;
        }

        /* ---- HOW IT WORKS ---- */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-6);
          position: relative;
        }
        .steps-grid::before {
          content: '';
          position: absolute;
          top: 44px;
          left: calc(33% - 8px);
          right: calc(33% - 8px);
          height: 2px;
          background: linear-gradient(90deg, var(--primary-200), var(--gold-300), var(--primary-200));
          z-index: 0;
        }
        .step-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-8);
          text-align: center;
          border: 1px solid var(--neutral-200);
          box-shadow: var(--shadow-sm);
          transition: all var(--transition-base);
          position: relative;
          z-index: 1;
        }
        .step-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-200);
        }
        .step-card--featured {
          border-color: var(--primary-300);
          box-shadow: var(--shadow-md);
          background: linear-gradient(135deg, white, var(--primary-50));
        }
        .step-card__number {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--primary-500);
          color: white;
          font-size: var(--text-sm);
          font-weight: var(--weight-bold);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: var(--shadow-sm);
        }
        .step-card__icon {
          font-size: 2.5rem;
          margin-bottom: var(--space-4);
          display: block;
        }
        .step-card h3 {
          font-size: var(--text-xl);
          color: var(--primary-800);
          margin-bottom: var(--space-3);
        }
        .step-card p {
          color: var(--neutral-600);
          font-size: var(--text-sm);
          line-height: var(--leading-relaxed);
          margin-bottom: var(--space-4);
        }
        .step-card__link {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--primary-500);
          text-decoration: none;
        }
        .step-card__link:hover {
          color: var(--primary-700);
        }

        /* ---- PROFILE GRID ---- */
        .profile-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: var(--space-5);
        }
        .profile-card {
          background: white;
          border-radius: var(--radius-xl);
          overflow: hidden;
          border: 1px solid var(--neutral-200);
          text-decoration: none;
          transition: all var(--transition-base);
          box-shadow: var(--shadow-xs);
        }
        .profile-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-300);
        }
        .profile-card__photo {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
          background: var(--neutral-100);
        }
        .profile-card__photo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-slow);
        }
        .profile-card:hover .profile-card__photo img {
          transform: scale(1.06);
        }
        .profile-card__verified {
          position: absolute;
          top: var(--space-2);
          right: var(--space-2);
          width: 26px;
          height: 26px;
          background: var(--success);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: var(--shadow-sm);
        }
        .profile-card__info {
          padding: var(--space-3) var(--space-4) var(--space-4);
        }
        .profile-card__info h3 {
          font-size: var(--text-base);
          font-weight: var(--weight-bold);
          color: var(--neutral-900);
          margin-bottom: var(--space-1);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .profile-card__meta {
          font-size: var(--text-xs);
          color: var(--neutral-600);
          margin: 0 0 var(--space-1);
        }
        .profile-card__location,
        .profile-card__edu {
          font-size: var(--text-xs);
          color: var(--neutral-500);
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .empty-profiles {
          text-align: center;
          padding: var(--space-16);
          grid-column: 1 / -1;
        }
        .empty-profiles span, .empty-news span {
          font-size: 3.5rem;
          display: block;
          margin-bottom: var(--space-4);
        }
        .empty-profiles h3, .empty-news h3 {
          font-size: var(--text-2xl);
          color: var(--neutral-800);
          margin-bottom: var(--space-2);
        }
        .empty-profiles p, .empty-news p {
          color: var(--neutral-600);
          margin-bottom: var(--space-6);
        }

        /* ---- NEWS GRID ---- */
        .news-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-template-rows: auto auto;
          gap: var(--space-5);
        }
        .news-card {
          background: white;
          border-radius: var(--radius-xl);
          overflow: hidden;
          border: 1px solid var(--neutral-200);
          text-decoration: none;
          transition: all var(--transition-base);
          box-shadow: var(--shadow-xs);
          display: flex;
          flex-direction: column;
        }
        .news-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-200);
        }
        .news-card--featured {
          grid-column: span 2;
          grid-row: span 1;
        }
        .news-card__image {
          aspect-ratio: 16/9;
          overflow: hidden;
          background: var(--neutral-100);
        }
        .news-card--featured .news-card__image {
          aspect-ratio: 16/8;
        }
        .news-card__image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-slow);
        }
        .news-card:hover .news-card__image img {
          transform: scale(1.04);
        }
        .news-card__body {
          padding: var(--space-4);
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
        .news-card__body h3 {
          font-size: var(--text-base);
          font-weight: var(--weight-bold);
          color: var(--neutral-900);
          margin: 0;
          line-height: var(--leading-snug);
        }
        .news-card--featured .news-card__body h3 {
          font-size: var(--text-xl);
        }
        .news-card__body p {
          font-size: var(--text-sm);
          color: var(--neutral-600);
          line-height: var(--leading-relaxed);
          margin: 0;
          flex: 1;
        }
        .news-card__meta {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          flex-wrap: wrap;
        }
        .news-category {
          font-size: 10px;
          font-weight: var(--weight-bold);
          color: var(--primary-600);
          background: var(--primary-50);
          padding: 2px var(--space-2);
          border-radius: var(--radius-full);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid var(--primary-100);
        }
        .news-date {
          font-size: var(--text-xs);
          color: var(--neutral-500);
        }
        .news-card__read-more {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--primary-500);
          margin-top: auto;
        }
        .empty-news {
          text-align: center;
          padding: var(--space-12);
          grid-column: 1 / -1;
        }

        /* ---- VALUES GRID ---- */
        .values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-6);
        }
        .value-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          border: 1px solid var(--neutral-200);
          box-shadow: var(--shadow-xs);
          transition: all var(--transition-base);
        }
        .value-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-200);
        }
        .value-card__icon {
          font-size: 2rem;
          margin-bottom: var(--space-4);
          display: block;
          width: 52px;
          height: 52px;
          background: var(--primary-50);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--primary-100);
        }
        .value-card h3 {
          font-size: var(--text-lg);
          color: var(--primary-800);
          margin-bottom: var(--space-2);
        }
        .value-card p {
          font-size: var(--text-sm);
          color: var(--neutral-600);
          line-height: var(--leading-relaxed);
          margin: 0;
        }

        /* ---- CTA SECTION ---- */
        .cta-section {
          position: relative;
          padding: var(--space-20) 0;
          overflow: hidden;
          background: linear-gradient(135deg, var(--primary-800) 0%, var(--primary-700) 40%, var(--gold-700) 100%);
        }
        .cta-section__bg {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='30' cy='30' r='1.5' fill='white' fill-opacity='0.05'/%3E%3C/svg%3E");
        }
        .cta-section__content {
          position: relative;
          text-align: center;
          max-width: 640px;
          margin: 0 auto;
        }
        .cta-section__pot {
          font-size: 3.5rem;
          display: block;
          margin-bottom: var(--space-4);
          animation: float 3s ease-in-out infinite;
        }
        .cta-section__content h2 {
          font-size: var(--text-5xl);
          color: white;
          margin-bottom: var(--space-4);
        }
        .cta-section__content p {
          font-size: var(--text-lg);
          color: rgba(255,255,255,0.85);
          line-height: var(--leading-relaxed);
          margin-bottom: var(--space-8);
        }
        .cta-section__actions {
          display: flex;
          gap: var(--space-4);
          justify-content: center;
          flex-wrap: wrap;
        }
        .btn-gold {
          background: linear-gradient(135deg, var(--gold-700), var(--gold-500));
          color: var(--earth-900);
          font-weight: var(--weight-bold);
          border: none;
          box-shadow: 0 8px 24px rgba(212,175,55,0.3);
        }
        .btn-gold:hover:not(:disabled) {
          background: linear-gradient(135deg, var(--gold-900), var(--gold-700));
          transform: translateY(-2px);
          color: var(--earth-900);
        }
        .btn-outline-light {
          background: transparent;
          color: white;
          border: 2px solid rgba(255,255,255,0.5);
        }
        .btn-outline-light:hover:not(:disabled) {
          background: rgba(255,255,255,0.1);
          border-color: white;
          color: white;
        }

        /* ---- RESPONSIVE ---- */
        @media (max-width: 1024px) {
          .news-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .news-card--featured {
            grid-column: span 2;
          }
          .values-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .steps-grid::before {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .hero__grid {
            grid-template-columns: 1fr;
            gap: var(--space-10);
          }
          .hero__visual {
            order: -1;
          }
          .hero__card {
            max-width: 100%;
          }
          .hero__title {
            font-size: var(--text-4xl);
          }
          .hero__float {
            display: none;
          }
          .hero__stats {
            flex-wrap: wrap;
            justify-content: center;
          }
          .heritage-band__dot {
            display: none;
          }
          .heritage-band__inner {
            gap: var(--space-4);
          }
          .steps-grid {
            grid-template-columns: 1fr;
          }
          .profile-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .news-grid {
            grid-template-columns: 1fr;
          }
          .news-card--featured {
            grid-column: span 1;
          }
          .values-grid {
            grid-template-columns: 1fr;
          }
          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--space-4);
          }
          .cta-section__content h2 {
            font-size: var(--text-4xl);
          }
        }

        @media (max-width: 480px) {
          .profile-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: var(--space-3);
          }
          .hero__actions {
            flex-direction: column;
          }
          .cta-section__actions {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;